/**
 * Sesión del Vault compartida por toda la pestaña.
 * El PIN vive solo en memoria del módulo: recargar la página o cerrar sesión lo borra.
 * La sesión pertenece a un usuario y a una organización; con otros, se lee como bloqueada.
 */

export const VAULT_SESSION_DURATION = 300 // 5 minutes in seconds

interface SesionVault {
  pin: string
  expiresAt: number
  orgId: string
  userSub: string
}

let sesion: SesionVault | null = null
let ticker: ReturnType<typeof setInterval> | undefined
let generacion = 0
let contexto = ''
const listeners = new Set<() => void>()

const notificar = () => {
  for (const listener of listeners) listener()
}

const segundosRestantes = (): number =>
  sesion ? Math.max(0, Math.round((sesion.expiresAt - Date.now()) / 1000)) : 0

const esDe = (orgId: string, userSub: string): boolean =>
  sesion !== null && sesion.orgId === orgId && sesion.userSub === userSub

export const suscribirVault = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Número que cambia cada vez que el Vault se bloquea, se desbloquea o cambian el usuario o la organización. */
export const generacionVault = (): number => generacion

/** Borra el PIN y avisa a todos los componentes. */
export const bloquearVault = (): void => {
  generacion += 1
  clearInterval(ticker)
  ticker = undefined
  if (!sesion) return
  sesion = null
  notificar()
}

/** Guarda el PIN por VAULT_SESSION_DURATION y avisa cada segundo hasta que vence. */
export const desbloquearVault = (pin: string, orgId: string, userSub: string): void => {
  generacion += 1
  clearInterval(ticker)
  sesion = { pin, orgId, userSub, expiresAt: Date.now() + VAULT_SESSION_DURATION * 1000 }
  ticker = setInterval(() => {
    if (segundosRestantes() <= 0) bloquearVault()
    else notificar()
  }, 1000)
  notificar()
}

/** Desbloquea solo si la generación sigue en `generacionInicial`. */
export const desbloquearSiNoCambio = (generacionInicial: number, pin: string, orgId: string, userSub: string): void => {
  if (generacion !== generacionInicial) return
  desbloquearVault(pin, orgId, userSub)
}

/** Segundos que le quedan a la sesión de ese usuario y organización. 0 si está bloqueada. */
export const tiempoRestanteVault = (orgId: string, userSub: string): number =>
  esDe(orgId, userSub) ? segundosRestantes() : 0

/** El PIN de la sesión vigente de ese usuario y organización, o null. */
export const pinVigente = (orgId: string, userSub: string): string | null =>
  tiempoRestanteVault(orgId, userSub) > 0 ? (sesion?.pin ?? null) : null

/**
 * Registra el usuario y la organización vigentes.
 * Si cambiaron, cambia la generación y descarta las respuestas de PIN que estaban en vuelo.
 * Bloquea si la sesión abierta es de otro usuario u otra organización.
 */
export const bloquearSiEsDeOtro = (orgId: string, userSub: string): void => {
  const nuevoContexto = `${orgId}|${userSub}`
  if (contexto !== nuevoContexto) {
    contexto = nuevoContexto
    generacion += 1
  }
  if (sesion && !esDe(orgId, userSub)) bloquearVault()
}
